import React, { useState, useEffect } from "react";
import "./Navigation.css";
import { useNavigate, useLocation, useParams } from "react-router-dom";

interface Region {
  company_code: string;
  region_code: string;
  region_name: string;
}

interface NavigationProps {
  regions: Region[];
  expandedRegion: string | null;
  selectedSubLink:
    | "manning"
    | "opportunity"
    | "project"
    | "openpros"
    | "recruiting";
  toggleRegion: (regionCode: string) => void;
  setSelectedSubLink: (
    link:
      | "manning"
      | "opportunity"
      | "project"
      | "openpros"
      | "recruiting"
  ) => void;
}

const Navigation: React.FC<NavigationProps> = ({
  regions,
  expandedRegion,
  selectedSubLink,
  toggleRegion,
  setSelectedSubLink,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { regionCode } = useParams<{ regionCode: string }>();

  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false); // ✅ prevents flicker

  // === Auto-select logic ===
  useEffect(() => {
    if (regions.length === 0 || initialized) return;

    // --- Recruiting route (National special case) ---
    if (location.pathname.includes("/recruiting")) {
      setSelectedRegion("NATIONAL");
      setSelectedSubLink("recruiting");
      if (expandedRegion !== "NATIONAL") toggleRegion("NATIONAL");
      setInitialized(true);
      return;
    }

    // --- Default to first region if none selected ---
    if (!selectedRegion && !regionCode) {
      const firstRegion = regions[0];
      setSelectedRegion(firstRegion.region_code);
      setSelectedSubLink("manning");
      toggleRegion(firstRegion.region_code);
      navigate(`/manning/${firstRegion.region_code}`);
      setInitialized(true);
      return;
    }

    // --- Sync regionCode from URL ---
    if (regionCode && regionCode !== selectedRegion) {
      setSelectedRegion(regionCode);
      toggleRegion(regionCode);
      setInitialized(true);
    }
  }, [
    regions,
    selectedRegion,
    regionCode,
    location.pathname,
    expandedRegion,
    toggleRegion,
    setSelectedSubLink,
    navigate,
    initialized,
  ]);

  /** ✅ Region Click Logic (fixed to always show Manning when switching from Recruiting) */
const handleRegionClick = (regionCode: string) => {
  const isRecruitingPage = location.pathname.includes("/recruiting");
  const isNational = regionCode === "NATIONAL";

  if (regionCode !== selectedRegion) {
    setSelectedRegion(regionCode);
    toggleRegion(regionCode);

    // ✅ NATIONAL defaults to Forecast
    if (isNational) {
      setSelectedSubLink("project");
      navigate("/project/NATIONAL");
      return;
    }

    // ✅ All other regions default to Manning
    setSelectedSubLink("manning");
    navigate(`/manning/${regionCode}`);
  } else {
    // Toggle collapse if same region clicked
    toggleRegion(regionCode);

    // ✅ If on recruiting and clicking a non-national region → Manning
    if (isRecruitingPage && !isNational) {
      setSelectedSubLink("manning");
      navigate(`/manning/${regionCode}`);
    }
  }
};


  const handleSubLinkClick = (
    link:
      | "manning"
      | "opportunity"
      | "project"
      | "openpros"
      | "recruiting",
    regionCode: string
  ) => {
    if (location.pathname !== `/${link}/${regionCode}`) {
      setSelectedSubLink(link);
      setSelectedRegion(regionCode);
      navigate(`/${link}/${regionCode}`);
    }
  };

  return (
    <aside className="landing-sidebar">
      {regions.length > 0 ? (
        <>
          {regions
          .map((region) => (
            <div key={region.region_code} className="region-item">
              <button
                className={`region-button ${
                  expandedRegion === region.region_code ? "expanded" : ""
                }`}
                onClick={() => handleRegionClick(region.region_code)}
              >
                {region.region_name}
                <span className="dropdown-icon">▼</span>
              </button>

              {expandedRegion === region.region_code && (
                <div className="sub-links">
                  <button
                    className={`sub-link ${
                      selectedSubLink === "manning" &&
                      selectedRegion === region.region_code
                        ? "selected"
                        : ""
                    }`}
                    onClick={() =>
                      handleSubLinkClick("manning", region.region_code)
                    }
                  >
                    Manning
                  </button>
                  <button
                    className={`sub-link ${
                      selectedSubLink === "opportunity" &&
                      selectedRegion === region.region_code
                        ? "selected"
                        : ""
                    }`}
                    onClick={() =>
                      handleSubLinkClick("opportunity", region.region_code)
                    }
                  >
                    Opportunity
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* === NATIONAL / ALL REGIONS === */}
          <div key="national-all" className="region-item">
            <button
              className={`region-button ${
                expandedRegion === "NATIONAL" ? "expanded" : ""
              }`}
              onClick={() => handleRegionClick("NATIONAL")}
            >
              National - All Regions
              <span className="dropdown-icon">▼</span>
            </button>

            {expandedRegion === "NATIONAL" && (
              <div className="sub-links">
                  <button
                    className={`sub-link ${
                      selectedSubLink === "project" &&
                      selectedRegion === "NATIONAL"
                        ? "selected"
                        : ""
                    }`}
                    onClick={() =>
                      handleSubLinkClick("project", "NATIONAL")
                    }
                  >
                    Forecast
                </button>
                <button
                  className={`sub-link ${
                    selectedSubLink === "manning" &&
                    selectedRegion === "NATIONAL"
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleSubLinkClick("manning", "NATIONAL")}
                >
                  IS National Manning
                </button>
                <button
                  className={`sub-link ${
                    selectedSubLink === "recruiting" &&
                    selectedRegion === "NATIONAL"
                      ? "selected"
                      : ""
                  }`}
                  onClick={() =>
                    handleSubLinkClick("recruiting", "NATIONAL")
                  }
                >
                  Recruiting
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <p>Loading regions...</p>
      )}
    </aside>
  );
};

export default Navigation;
